import { codeChallengeReducer, initialChallenge, openSessionOf } from '../../domain/codeChallenge';
import type { ChallengeEvent, ChallengeSession, ChallengeState, WaitReason } from '../../domain/codeChallenge';
import { identifierKind, identifierProblem, normalisePhoneInput, submittableIdentifier } from '../../domain/identifier';
import type { CodePurpose, CodeSent, IdentifierKind, OpenedSession, PasscodeChannel, PasswordGrant } from '../../types/domain.types';

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const PHONE_TYPING = /^[\d\s+]+$/;
const DIGITS_ONLY = /^\d+$/;

export interface CodeAsk {
  readonly identifier: string;
  readonly purpose: CodePurpose;
  readonly channel?: PasscodeChannel;
}

export type LoginChallenge = ChallengeState<CodeAsk, CodeSent, never>;
export type LoginCodeSession = ChallengeSession<CodeAsk, CodeSent>;

export type CodeOnlyReason = 'NO_PASSWORD_SET' | 'USE_CODE';

export type LoginStep =
  | { readonly screen: 'identify' }
  | { readonly screen: 'password'; readonly password: string; readonly missingKey: number }
  | { readonly screen: 'noPassword'; readonly reason: CodeOnlyReason }
  | { readonly screen: 'locked'; readonly lockUntil: number; readonly lockMinutes: number }
  | { readonly screen: 'newPassword'; readonly grant: PasswordGrant; readonly session: OpenedSession }
  | { readonly screen: 'done' };

export type LoginScreen = LoginStep['screen'] | 'code';

export interface LoginField {
  readonly value: string;
  readonly attempted: boolean;
  readonly problemKey: number;
  readonly lookup: string | null;
}

export interface LoginState {
  readonly field: LoginField;
  readonly step: LoginStep;
  readonly challenge: LoginChallenge;
  readonly epoch: number;
}

export type LoginEvent =
  | { readonly type: 'typed'; readonly value: string }
  | { readonly type: 'settled'; readonly value: string }
  | { readonly type: 'problemFound' }
  | { readonly type: 'codeSent'; readonly epoch: number; readonly ask: CodeAsk; readonly sent: CodeSent; readonly at: number }
  | { readonly type: 'codeTyped'; readonly code: string }
  | { readonly type: 'codeRefused' }
  | { readonly type: 'sendHeld'; readonly epoch: number; readonly ask: CodeAsk; readonly reason: WaitReason; readonly seconds: number; readonly at: number }
  | { readonly type: 'locked'; readonly epoch: number; readonly ask: CodeAsk; readonly seconds: number; readonly at: number }
  | { readonly type: 'passwordChosen'; readonly epoch: number }
  | { readonly type: 'passwordTyped'; readonly password: string }
  | { readonly type: 'passwordMissing' }
  | { readonly type: 'noPassword'; readonly epoch: number; readonly reason: CodeOnlyReason }
  | { readonly type: 'sessionOpened'; readonly session: OpenedSession }
  | { readonly type: 'finished' }
  | { readonly type: 'back' };

export const askKeyOf = ({ identifier, purpose }: Pick<CodeAsk, 'identifier' | 'purpose'>): string => `${purpose}:${identifier}`;

export const fieldValueOf = (raw: string): string => (PHONE_TYPING.test(raw) ? normalisePhoneInput(raw) : raw);

export const typedKindOf = (value: string): IdentifierKind | null => {
  if (value.trim() === '') return null;
  return DIGITS_ONLY.test(value) ? 'PHONE' : 'EMAIL';
};

export const submittableOf = (value: string): string | null => {
  const kind = identifierKind(value);
  if (kind === null || identifierProblem(kind, value) !== null) return null;
  return submittableIdentifier(kind, value);
};

export const initialLoginState = (): LoginState => ({
  field: { value: '', attempted: false, problemKey: 0, lookup: null },
  step: { screen: 'identify' },
  challenge: initialChallenge<CodeAsk, CodeSent, never>(),
  epoch: 0,
});

const IDENTIFY: LoginStep = { screen: 'identify' };
const FORM_SCREENS: readonly LoginScreen[] = ['identify', 'password', 'noPassword'];
const FINAL_SCREENS: readonly LoginScreen[] = ['done', 'newPassword'];

const isFinal = (step: LoginStep): boolean => FINAL_SCREENS.includes(step.screen);
const onForm = (step: LoginStep): boolean => FORM_SCREENS.includes(step.screen);

export const codeSessionOf = (state: LoginState): LoginCodeSession | null => (onForm(state.step) ? openSessionOf(state.challenge.phase) : null);

export const screenOf = (state: LoginState): LoginScreen => (codeSessionOf(state) === null ? state.step.screen : 'code');

export const viewScreenOf = (state: LoginState, showCode: boolean): LoginScreen => {
  const screen = screenOf(state);
  return screen === 'code' && !showCode ? state.step.screen : screen;
};

const withChallenge = (state: LoginState, event: ChallengeEvent<CodeAsk, CodeSent, never>): LoginState => ({
  ...state,
  challenge: codeChallengeReducer(state.challenge, event),
});

const current = (state: LoginState, epoch: number): boolean => state.epoch === epoch && !isFinal(state.step);

const lockOver = (step: LoginStep, at: number): boolean => step.screen !== 'locked' || step.lockUntil <= at;

export const acceptsSend = (state: LoginState, epoch: number, at: number): boolean => current(state, epoch) && lockOver(state.step, at);

const locked = (state: LoginState, event: Extract<LoginEvent, { type: 'locked' }>): LoginState => {
  const held = withChallenge(state, { type: 'locked', key: askKeyOf(event.ask), seconds: event.seconds, at: event.at });
  if (!current(state, event.epoch)) return held;
  return {
    ...held,
    step: {
      screen: 'locked',
      lockUntil: event.at + event.seconds * MS_PER_SECOND,
      lockMinutes: Math.max(1, Math.ceil(event.seconds / SECONDS_PER_MINUTE)),
    },
  };
};

export const loginReducer = (state: LoginState, event: LoginEvent): LoginState => {
  switch (event.type) {
    case 'typed': {
      const field = { ...state.field, value: fieldValueOf(event.value) };
      const step = state.step.screen === 'noPassword' ? IDENTIFY : state.step;
      return { ...state, field, step, epoch: state.epoch + 1 };
    }
    case 'settled':
      return state.field.value === event.value ? { ...state, field: { ...state.field, lookup: submittableOf(event.value) } } : state;
    case 'problemFound':
      return { ...state, field: { ...state.field, attempted: true, problemKey: state.field.problemKey + 1 } };
    case 'codeSent': {
      if (!acceptsSend(state, event.epoch, event.at)) return state;
      const landed = withChallenge(state, {
        type: 'sent',
        epoch: state.challenge.epoch,
        key: askKeyOf(event.ask),
        target: { ...event.ask, channel: event.sent.sentOn },
        sent: event.sent,
        at: event.at,
      });
      return state.step.screen === 'locked' ? { ...landed, step: IDENTIFY } : landed;
    }
    case 'codeTyped':
      return withChallenge(state, { type: 'typed', code: event.code });
    case 'codeRefused':
      return withChallenge(state, { type: 'refused' });
    case 'sendHeld':
      if (isFinal(state.step)) return state;
      if (event.reason === 'RESEND_TOO_SOON' && state.epoch !== event.epoch) return state;
      return withChallenge(state, {
        type: 'held',
        epoch: state.challenge.epoch,
        key: askKeyOf(event.ask),
        reason: event.reason,
        seconds: event.seconds,
        channel: event.ask.channel,
        at: event.at,
      });
    case 'locked':
      return locked(state, event);
    case 'passwordChosen':
      return current(state, event.epoch) && onForm(state.step)
        ? { ...state, step: { screen: 'password', password: state.step.screen === 'password' ? state.step.password : '', missingKey: 0 } }
        : state;
    case 'passwordTyped':
      return state.step.screen === 'password' ? { ...state, step: { ...state.step, password: event.password } } : state;
    case 'passwordMissing':
      return state.step.screen === 'password' ? { ...state, step: { ...state.step, missingKey: state.step.missingKey + 1 } } : state;
    case 'noPassword':
      return current(state, event.epoch) && onForm(state.step) ? { ...state, step: { screen: 'noPassword', reason: event.reason } } : state;
    case 'sessionOpened':
      if (isFinal(state.step)) return state;
      return {
        ...state,
        step: event.session.passwordGrant === null ? { screen: 'done' } : { screen: 'newPassword', grant: event.session.passwordGrant, session: event.session },
      };
    case 'finished':
      return state.step.screen === 'newPassword' ? { ...state, step: { screen: 'done' } } : state;
    case 'back':
      if (isFinal(state.step)) return state;
      return { ...withChallenge(state, { type: 'cancelled' }), step: IDENTIFY, epoch: state.epoch + 1 };
  }
};
